import ComponentState, { ComponentStateTypes } from "@/components/common/ComponentState";
import { AccommodationStatesModel } from "@/types/acommodation/acc-stats-types";
import { useEffect, useState } from "react";
import RoomTypeView from "../../roomType/RoomTypeView";
import HTMLCode from "@/components/common/HTMLCode";
import { api } from "@/config/axios-context";
import useAppContext from "@/utilities/hooks/useAppContext";
import PropertyView from "@/components/common/views/PropertyView";
import AppPieChart from "@/components/common/charts/AppPieChart";
import { useParams, useSearchParams } from "react-router-dom";
import { URI_PARAMETER_KEYS } from "@/config/router";
import { months } from "@/utilities/calendar-utilities";

export type AccommodationOverviewStatisticsProps = {
  targetSeasonId?: string | number;
  month?: number;
  untilDate?: Date;
  setSeason?: (season: { id: number; name: string; active: boolean }) => void;
};

const AccommodationOverviewStatistics = (props: AccommodationOverviewStatisticsProps) => {
  const context = useAppContext();
  const [searchQueryParams] = useSearchParams();
  var params = useParams<{ [URI_PARAMETER_KEYS.TARGET_SEASON_ID]: string }>();
  const [componentState, setComponentState] = useState<ComponentStateTypes>("loading");
  const [data, setData] = useState<AccommodationStatesModel>();

  // #region loadData
  useEffect(() => {
    loadData().then();
  }, [props.targetSeasonId, params[URI_PARAMETER_KEYS.TARGET_SEASON_ID], searchQueryParams]);

  const loadData = async () => {
    const year = new Date().getFullYear();
    let fromDate: string | undefined = undefined;
    let toDate: string | undefined = undefined;
    const filterType = searchQueryParams.get('filterType');
    switch (filterType) {
      case 'month':
        const monthFilter = searchQueryParams.get('monthFilter');
        const monthIndex = months.findIndex(m => m === monthFilter);
        fromDate = `${year}-${(monthIndex + 1).toString().padStart(2, '0')}-01`;
        toDate = `${year}-${(monthIndex + 1).toString().padStart(2, '0')}-${new Date(year, monthIndex + 1, 0).getDate()}`;

        break;
      case 'between':
        const filterDates = searchQueryParams.get('filterDates')?.split('-');
        fromDate = filterDates?.[0];
        toDate = filterDates?.[1];
        break;
      case 'until':
        const untilDate = searchQueryParams.get('untilDate');
        fromDate = `${year - 1}-01-01`;
        toDate = untilDate || undefined;
        break;
    }
    
    try {
      const response = await api.get<AccommodationStatesModel>({
        url: "AccommodationStates/States",
        params: {
          seasonId: props.targetSeasonId,
          fromDate: fromDate || '',
          toDate: toDate || ''
        }
      });

      if (!response.success) {
        throw response.statusText;
      }

      setData(response.data!);
      props.setSeason?.({
        id: response.data!.seasonId,
        name: response.data!.seasonName,
        active: response.data!.isCurrentSeasonActives,
      });
      setComponentState("");
    } catch (error: any) {
      context.notify("error", "request.load.error", Array.isArray(error) ? error[0].message : error.message);
      setComponentState("nodata");
      throw error;
    }
  };
  // #endregion loadData

  const printRoomTypeData = () => {
    if (!data) return <></>;
    const roomTypes: React.ReactNode[] = [];
    data?.booking.roomTypeData.forEach((item) => {
      roomTypes.push(
        <div key={`roomType-${item.name}`}>
          {" "}
          <RoomTypeView name={item.name!} color={item.color} />: {item.count} - {item.totalSum.toFixed(2)} <HTMLCode code="euro" /> -{" "}
          {item.percent.toFixed(1)}%
        </div>,
      );
    });

    return <>{roomTypes}</>;
  };

  const printChannelData = () => {
    if (!data) return <></>;
    const result = data.booking.channelsData.items!.map(item => {
      return (
        <div key={`channel-${item.name}`}>
          {" "}
          - {item.name}: {item.count} - {item.totalSum.toFixed(2)} <HTMLCode code="euro" /> - {item.percent.toFixed(1)}%
        </div>
      )
    });

    return <>
      {result}
    </>;
  }

  return (
    <ComponentState state={componentState}>
      <div className="px-2 md:w-max">
        <h2>{context.t("accommodation.stats.profit")}</h2>
        <hr />
        <PropertyView
          label="agriculture.stats.bruto"
          value={
            <>
              {data?.bruto.toFixed(2)} <HTMLCode code="euro" />
            </>
          }
        />
        <PropertyView
          label="agriculture.stats.neto"
          value={
            <>
              {data?.neto.toFixed(2)} <HTMLCode code="euro" />
            </>
          }
        />
        <PropertyView
          label="accommodation.stats.bookingAverageTotal"
          value={
            <>
              {data?.booking.totalAverage.toFixed(2)} <HTMLCode code="euro" />
            </>
          }
        />
        <PropertyView
          label="accommodation.stats.nightsTotalAverage"
          value={
            <>
              {data?.booking.nights} / {data?.booking.nightsAverage.toFixed(2)}
            </>
          }
        />
        {(data?.booking.roomTypeStats?.length ?? 0) > 1 && (
          <PropertyView>
            {data?.booking.roomTypeStats.map((roomTypeStat) => {
              return (
                <div key={`acc-stats-nightsTotalAverag-${roomTypeStat.id}`} className="flex">
                  <div className="w-4 h-4 my-auto mr-2" style={{ backgroundColor: roomTypeStat.color }}></div>
                  <span>
                    {roomTypeStat.name} - {roomTypeStat.stats?.nights} / {roomTypeStat.stats?.nightsAverage.toFixed(2)}
                  </span>
                </div>
              );
            })}
          </PropertyView>
        )}
        <PropertyView
          label="accommodation.stats.brutoProfitPastFuture"
          value={
            <>
              {data?.booking.paidSum.toFixed(2)} / {data?.booking.toBePaidSum.toFixed(2)}
            </>
          }
        />
        <PropertyView
          label="accommodation.stats.goneStayingIncomingTotal"
          value={
            <>
              {data?.booking.gone} | {data?.booking.staying} | {data?.booking.incoming} | {data?.booking.count}
            </>
          }
        />
        {(data?.booking.roomTypeStats?.length ?? 0) > 1 && (
          <div className="flex flex-wrap">
            <PropertyView label="fields.roomType.name" value={printRoomTypeData()} />
            <div className="w-min">
              <AppPieChart
                inPieLabel="value"
                style={{ height: "150px" }}
                data={
                  data?.booking.roomTypeData.map((item) => {
                    return {
                      name: item.name ?? "",
                      value: item.count,
                      color: item.color,
                    };
                  }) ?? []
                }
              />
            </div>
          </div>
        )}
        <PropertyView
          label="accommodation.stats.cancelledBruto"
          value={
            <div className="text-red-700 dark:text-red-300">
              {data?.booking.canceled.count} / {data?.booking.canceled.totalSum.toFixed(2)} - {data?.booking.canceled.percent.toFixed(1)}%
            </div>
          }
        />
        <h2 className="pt-5">{context.t("pages.accommodation.additionalCharges.view")}</h2>
        <hr />
        <PropertyView label="common.count" value={data?.booking.additionalCharge.count} />
        <PropertyView
          label="fields.total.name"
          value={
            <>
              {data?.booking.additionalCharge.totalSum.toFixed(2)} <HTMLCode code="euro" />
            </>
          }
        />
        <PropertyView
          label="accommodation.stats.income"
          value={
            <>
              {data?.booking.additionalCharge.income.toFixed(2)} <HTMLCode code="euro" />
            </>
          }
        />
        <PropertyView
          label="accommodation.stats.expenses"
          value={
            <>
              {data?.booking.additionalCharge.expenses.toFixed(2)} <HTMLCode code="euro" />
            </>
          }
        />

        <h2 className="pt-5">{context.t("accommodation.stats.expenses")}</h2>
        <hr />
        <div className="flex flex-wrap">
          <PropertyView label="accommodation.stats.channelExpenses" value={printChannelData()} />
          <div className="w-min">
            <AppPieChart
              inPieLabel="value"
              legend="right"
              style={{ height: "150px" }}
              data={
                data?.booking.channelsData.items!.map((item) => {
                  console.log(item);
                  return {
                    name: item.name ?? "",
                    value: item.count,
                    color: item.color,
                  };
                }) ?? []
              }
            />
          </div>
        </div>
        <PropertyView
          label="accommodation.stats.workExpenses"
          value={
            <>
              {data?.work.totalSum.toFixed(2)} <HTMLCode code="euro" />
            </>
          }
        />
        <PropertyView
          label="accommodation.stats.workMaintenanceExpenses"
          value={
            <>
              {data?.work.maintenanceTotalSum.toFixed(2)} <HTMLCode code="euro" />
            </>
          }
        />
        <PropertyView
          label="accommodation.stats.workInvestmentExpenses"
          value={
            <>
              {data?.work.investmentTotalSum.toFixed(2)} <HTMLCode code="euro" />
            </>
          }
        />
        <PropertyView label="accommodation.stats.workInterferenceCount" value={<>{data?.work.count}</>} />
      </div>
    </ComponentState>
  );
};

export default AccommodationOverviewStatistics;
